import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import MuiBox from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiList from '@mui/material/List';
import MuiListItemButton from '@mui/material/ListItemButton';
import MuiListItemIcon from '@mui/material/ListItemIcon';
import { useId, type ReactElement, type ReactNode } from 'react';
import { Divider } from '../Divider';
import { NavDrawerLink } from './NavDrawerLink';
import { drawerSx, iconSx, navSx, rowSx } from './navDrawerStyles';
import { Tooltip } from './Tooltip';

export { NAV_DRAWER_WIDTH, NAV_DRAWER_COLLAPSED_WIDTH } from './navDrawerStyles';

export interface NavDrawerItem {
  href: `/${string}`;
  label: string;
  icon: ReactNode;
  current: boolean;
}

export interface NavDrawerProps {
  // Names the landmark («Producto»).
  'aria-label': string;
  items: readonly NavDrawerItem[];
  // Takes over a plain primary click so the host routes in-app; modified clicks stay the browser's.
  onNavigate: (href: `/${string}`) => void;
  // Controlled: the host owns, and persists, whether the drawer is folded to its icons.
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  collapseLabel: string;
  expandLabel: string;
}

// A surface's persistent side navigation: a named landmark of real links, foldable to an icon rail.
// Permanent, so it traps no focus; no `sx`, because it is fixed chrome.
export function NavDrawer({
  items,
  onNavigate,
  collapsed,
  onCollapsedChange,
  collapseLabel,
  expandLabel,
  'aria-label': ariaLabel,
}: NavDrawerProps): ReactElement {
  const listId = useId();
  const toggleLabel = collapsed ? expandLabel : collapseLabel;
  return (
    <MuiDrawer variant="permanent" anchor="left" sx={drawerSx(collapsed)}>
      <MuiBox component="nav" aria-label={ariaLabel} sx={navSx}>
        {/* Glyph-only in both states, so the tooltip is its visible name. */}
        <Tooltip title={toggleLabel}>
          <MuiListItemButton
            component="button"
            sx={rowSx}
            aria-controls={listId}
            aria-label={toggleLabel}
            onClick={() => {
              onCollapsedChange(!collapsed);
            }}
          >
            <MuiListItemIcon sx={iconSx}>
              {/* The drawer's own collapse glyphs; the public chevrons mean «step through a sequence». */}
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </MuiListItemIcon>
          </MuiListItemButton>
        </Tooltip>
        <Divider />
        <MuiList id={listId} disablePadding>
          {items.map((item) => (
            <li key={item.href}>
              <NavDrawerLink item={item} collapsed={collapsed} onNavigate={onNavigate} />
            </li>
          ))}
        </MuiList>
      </MuiBox>
    </MuiDrawer>
  );
}
